import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import useAuth from "../helper/useAuth";
import PasswordField from "./PasswordField";

function pickLoginErrorMessage(data, t) {
  if (!data) return t("authModal.login.errors.generic");
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (data.errors && typeof data.errors === "object") {
    for (const v of Object.values(data.errors)) {
      const arr = Array.isArray(v) ? v : [v];
      const first = arr.find((x) => typeof x === "string" && x.trim());
      if (first) return first;
    }
  }
  return t("authModal.login.errors.generic");
}

function extractErrorCode(data) {
  if (!data || typeof data !== "object") return null;
  // Common shapes: { error: { code: "..." } } or { code: "..." }.
  return data.error?.code || data.code || null;
}

function LoginModalForm({
  onSwitchToRegister,
  onLoginSuccess,
  onForgotPassword,
  onEmailNotVerified,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { saveToken } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [message, setMessage] = useState(null);
  const [postRegisterNotice, setPostRegisterNotice] = useState(false);
  const [passwordChangedNotice, setPasswordChangedNotice] = useState(false);

  const initialValues = {
    email: "",
    password: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t("authModal.login.errors.emailInvalid"))
      .required(t("authModal.login.errors.emailRequired")),
    password: Yup.string().required(t("authModal.login.errors.passwordRequired")),
  });

  const handleEmailNotVerified = (email, password) => {
    if (typeof onEmailNotVerified === "function") {
      onEmailNotVerified(email, password);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      email: values.email.trim(),
      password: values.password,
      nomio_partner: true,
    };
    const locale = getSecondApiLocale(i18n.language);

    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/login`,
        payload,
      );

      if (data?.isSuccess === true && data?.token) {
        saveToken(data.token);
        resetForm();
        if (typeof onLoginSuccess === "function") {
          onLoginSuccess();
        }
        navigate(`/${i18n.language}/profile`, { replace: true });
      } else if (extractErrorCode(data) === "auth.email_not_verified") {
        // User exists but email isn't verified — open verify modal.
        // Pass the password too so the verify modal can call /auth/login
        // again on "Resend" to trigger a fresh code email.
        handleEmailNotVerified(payload.email, payload.password);
      } else {
        setMessage({
          status: false,
          text: pickLoginErrorMessage(data, t),
        });
      }
    } catch (error) {
      const data = error.response?.data;
      if (extractErrorCode(data) === "auth.email_not_verified") {
        handleEmailNotVerified(payload.email, payload.password);
      } else if (error.code === "ERR_NETWORK" || !error.response) {
        setMessage({
          status: false,
          text: t("authModal.login.errors.network"),
        });
      } else {
        setMessage({
          status: false,
          text: pickLoginErrorMessage(data, t),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (searchParams.get("registered") !== "1") return;
    setPostRegisterNotice(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("registered");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("passwordChanged") !== "1") return;
    setPasswordChangedNotice(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("passwordChanged");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!postRegisterNotice) return;
    const timer = setTimeout(() => setPostRegisterNotice(false), 12000);
    return () => clearTimeout(timer);
  }, [postRegisterNotice]);

  useEffect(() => {
    if (!passwordChangedNotice) return;
    const timer = setTimeout(() => setPasswordChangedNotice(false), 12000);
    return () => clearTimeout(timer);
  }, [passwordChangedNotice]);

  return (
    <>
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="row gy-4">
          {postRegisterNotice && (
            <div className="col-12 font-14 alert alert-success mb-0 py-3" role="status">
              {t("authModal.login.postRegister")}
            </div>
          )}
          {passwordChangedNotice && (
            <div className="col-12 font-14 alert alert-success mb-0 py-3" role="status">
              {t("authModal.login.postPasswordChanged")}
            </div>
          )}
          {message && (
            <div
              className={`col-12 font-14 ${
                message.status ? "text-success" : "text-danger"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="col-12">
            <label className="form-label mb-2 font-18 font-heading fw-600">
              {t("authModal.login.labels.email")}
            </label>
            <div className="position-relative">
              <Field
                type="email"
                name="email"
                autoComplete="email"
                className="common-input common-input--withIcon"
                placeholder={t("authModal.login.placeholders.email")}
              />
              <span className="input-icon">
                <img src="../assets/images/icons/envelope-icon.svg" alt="" />
              </span>
            </div>
            <ErrorMessage
              name="email"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
              <label className="form-label mb-0 font-18 font-heading fw-600">
                {t("authModal.login.labels.password")}
              </label>
              {typeof onForgotPassword === "function" && (
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="link text-main text-decoration-underline fw-500 bg-transparent border-0 p-0 font-14"
                >
                  {t("authModal.login.forgotPassword")}
                </button>
              )}
            </div>
            <PasswordField
              name="password"
              autoComplete="current-password"
              placeholder={t("authModal.login.placeholders.password")}
            />
            <ErrorMessage
              name="password"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-main btn-lg w-100 pill"
            >
              {isSubmitting
                ? t("authModal.login.btnLoading")
                : t("authModal.login.btn")}
            </button>
          </div>

          {typeof onSwitchToRegister === "function" && (
            <div className="col-12">
              <p className="text font-14 mb-0 text-center">
                {t("authModal.login.noAccount")}{" "}
                <button
                  type="button"
                  className="link text-main text-decoration-underline fw-500 bg-transparent border-0 p-0"
                  onClick={onSwitchToRegister}
                >
                  {t("authModal.login.goRegister")}
                </button>
              </p>
            </div>
          )}
        </Form>
      )}
    </Formik>
    </>
  );
}

export default LoginModalForm;
